<template>
  <modal-base
    :show="value"
    :title="$t('MergeDocuments')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    max-width="420"
    @on-cancel="$emit('input', false)"
  >

    <div class="overline grey--text text--darken-1 mb-1">{{ $t('From') }}</div>
    <v-sheet rounded color="grey lighten-4" class="pa-3 d-flex align-center mb-5">
      <v-icon small color="primary" class="mr-2">mdi-file-pdf-box</v-icon>
      <span class="text-body-2 text-truncate flex-grow-1">{{ sourceDoc && sourceDoc.name }}</span>
      <v-chip x-small outlined color="primary" class="ml-2 flex-shrink-0">
        {{ $t('Page') }} {{ sourcePage && sourcePage.index }}
      </v-chip>
    </v-sheet>

    <div class="overline grey--text text--darken-1 mb-1">{{ $t('Site.PdfManipulation.MergeInto') }}</div>
    <v-select
      v-model="targetDocId"
      :items="targetDocs"
      item-text="name"
      item-value="id"
      dense outlined hide-details
      prepend-inner-icon="mdi-file-pdf-box"
      class="mb-5"
    />

    <div class="overline grey--text text--darken-1 mb-2">{{ $t('Site.PdfManipulation.Pages') }}</div>
    <v-btn-toggle
      v-model="mergeMode"
      mandatory dense
      class="d-flex mb-2"
      style="width: 100%"
    >
      <v-btn value="single" small depressed class="flex-grow-1 text-none" :color="mergeMode === 'single' ? 'primary' : 'grey lighten-2'">
        <span :class="mergeMode === 'single' ? 'white--text' : 'grey--text text--darken-3'">{{ $t('Site.PdfManipulation.OnlyThisPage') }}</span>
      </v-btn>
      <v-btn value="all" small depressed class="flex-grow-1 text-none" :color="mergeMode === 'all' ? 'primary' : 'grey lighten-2'">
        <span :class="mergeMode === 'all' ? 'white--text' : 'grey--text text--darken-3'">{{ $t('Site.PdfManipulation.AllPages') }}</span>
      </v-btn>
      <v-btn value="custom" small depressed class="flex-grow-1 text-none" :color="mergeMode === 'custom' ? 'primary' : 'grey lighten-2'">
        <span :class="mergeMode === 'custom' ? 'white--text' : 'grey--text text--darken-3'">{{ $t('Site.PdfManipulation.SelectPages') }}</span>
      </v-btn>
    </v-btn-toggle>

    <v-sheet v-if="mergeMode === 'single'" rounded color="grey lighten-4" class="pa-2 d-flex align-center mb-5">
      <v-icon x-small color="primary" class="mr-1">mdi-file-outline</v-icon>
      <span class="text-caption primary--text">
        {{ $t('Page') }} {{ sourcePage && sourcePage.index }}
      </span>
    </v-sheet>

    <v-sheet v-else-if="mergeMode === 'all'" rounded color="grey lighten-4" class="pa-2 d-flex align-center mb-5">
      <v-icon x-small color="primary" class="mr-1">mdi-file-multiple-outline</v-icon>
      <span class="text-caption primary--text">{{ sourcePageCount }} {{ $t('Site.PdfManipulation.Pages') }}</span>
    </v-sheet>

    <v-sheet v-else rounded color="grey lighten-4" class="pa-2 mb-5" style="max-height: 140px; overflow-y: auto;">
      <v-row dense>
        <v-col v-for="page in sourcePages" :key="page.index" cols="4">
          <v-checkbox
            v-model="selectedPageIndices"
            :value="page.index"
            dense hide-details
            color="primary"
            class="mt-0"
          >
            <template v-slot:label>
              <span class="text-caption">{{ $t('Page') }} {{ page.index }}</span>
            </template>
          </v-checkbox>
        </v-col>
      </v-row>
    </v-sheet>

    <div class="overline grey--text text--darken-1 mb-2">{{ $t('Action') }}</div>
    <v-btn-toggle
      v-model="keepOriginal"
      mandatory dense
      class="d-flex"
      style="width: 100%"
    >
      <v-btn
        :value="false"
        small depressed
        class="flex-grow-1 text-none"
        :disabled="moveDisabled"
        :color="keepOriginal === false ? 'primary' : 'grey lighten-2'"
      >
        <v-icon x-small class="mr-1" :color="keepOriginal === false ? 'white' : 'grey darken-3'">mdi-file-move-outline</v-icon>
        <span :class="keepOriginal === false ? 'white--text' : 'grey--text text--darken-3'">{{ $t('Site.PdfManipulation.Move') }}</span>
      </v-btn>
      <v-btn
        :value="true"
        small depressed
        class="flex-grow-1 text-none"
        :color="keepOriginal === true ? 'primary' : 'grey lighten-2'"
      >
        <v-icon x-small class="mr-1" :color="keepOriginal === true ? 'white' : 'grey darken-3'">mdi-content-copy</v-icon>
        <span :class="keepOriginal === true ? 'white--text' : 'grey--text text--darken-3'">{{ $t('Site.PdfManipulation.Copy') }}</span>
      </v-btn>
    </v-btn-toggle>
    <div class="text-caption mt-1" :class="moveDisabled ? 'orange--text text--darken-2' : 'grey--text'">
      <span v-if="moveDisabled">{{ $t('Site.PdfManipulation.MoveRequiresDeletePermission') }}</span>
      <span v-else-if="!keepOriginal">{{ $t('Site.PdfManipulation.MovePagesDescription') }}</span>
      <span v-else>{{ $t('Site.PdfManipulation.CopyPagesDescription') }}</span>
    </div>

    <template v-slot:actions>
      <v-btn text small @click="$emit('input', false)">{{ $t('Cancel') }}</v-btn>
      <v-btn small color="primary" depressed :disabled="!canConfirm" @click="confirm">
        <v-icon x-small class="mr-1">mdi-merge</v-icon>
        {{ $t('Merge') }}
      </v-btn>
    </template>

  </modal-base>
</template>

<script>
import ModalBase from '@/components/base/Modal.vue';

export default {
    name: 'PdfMergeDialog',
    components: { ModalBase },
    props: {
        value: { type: Boolean, default: false },
        sourceDoc: { type: Object, default: null },
        sourcePage: { type: Object, default: null },
        targetDocs: { type: Array, default: () => [] },
        canDelete: { type: Boolean, default: true },
    },
    data() {
        return {
            targetDocId: null,
            mergeMode: 'single',
            selectedPageIndices: [],
            keepOriginal: false,
        };
    },
    computed: {
        sourcePages() {
            if (!this.sourceDoc) return [];
            return this.sourceDoc.pages;
        },
        sourcePageCount() {
            if (!this.sourceDoc) return 0;
            return this.sourceDoc.pages.length;
        },
        allPagesSelected() {
            if (!this.sourceDoc) return false;
            if (this.mergeMode === 'all') return true;
            if (this.mergeMode === 'single') return this.sourceDoc.pages.length === 1;
            if (this.mergeMode === 'custom') return this.selectedPageIndices.length === this.sourceDoc.pages.length;
            return false;
        },
        moveDisabled() {
            return this.allPagesSelected && !this.canDelete;
        },
        canConfirm() {
            if (!this.targetDocId) return false;
            if (this.mergeMode === 'custom' && this.selectedPageIndices.length === 0) return false;
            return true;
        },
    },
    watch: {
        value(v) {
            if (v) {
                this.targetDocId = null;
                this.mergeMode = 'single';
                this.selectedPageIndices = this.sourcePage ? [this.sourcePage.index] : [];
                this.keepOriginal = this.moveDisabled;
            }
        },
        moveDisabled(val) {
            if (val) this.keepOriginal = true;
        },
    },
    methods: {
        confirm() {
            var pageIndices;
            if (this.mergeMode === 'single') {
                pageIndices = this.sourcePage ? [this.sourcePage.index] : null;
            } else if (this.mergeMode === 'all') {
                pageIndices = null;
            } else {
                pageIndices = this.selectedPageIndices.slice();
            }
            this.$emit('merge', {
                targetDocId: this.targetDocId,
                pageIndices: pageIndices,
                keepOriginal: this.keepOriginal,
            });
        },
    },
};
</script>
